import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, Loader2 } from "lucide-react";
import { EmptyState } from "../comms/EmptyState";
import { RetryBanner } from "../comms/RetryBanner";
import { relativeTime } from "../../utils/oversightFormat";
import { describeNotification } from "./notificationText";
import { FEED_STATUS, useNotificationFeed } from "./useNotificationFeed";

const BADGE_CAP = 9;
const PANEL_WIDTH_PX = 360;
const PANEL_GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 8;

const badgeLabel = (count) => (count > BADGE_CAP ? `${BADGE_CAP}+` : String(count));

/**
 * Where the panel opens: just right of the sidebar, level with the bell. The
 * sidebar's wrapper clips overflow, so the panel is portalled and fixed.
 */
function panelPosition(button) {
  const rect = button.getBoundingClientRect();
  const sidebarRight = button.closest("aside")?.getBoundingClientRect().right ?? rect.right;
  const maxLeft = window.innerWidth - PANEL_WIDTH_PX - VIEWPORT_MARGIN_PX;
  return {
    top: Math.max(VIEWPORT_MARGIN_PX, rect.top),
    left: Math.max(VIEWPORT_MARGIN_PX, Math.min(sidebarRight + PANEL_GAP_PX, maxLeft)),
  };
}

function NotificationItem({ item, onOpen }) {
  const { title, body } = describeNotification(item);
  const unread = !item.readAt;
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item)}
        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-primary ${
          unread ? "bg-orange-50 hover:bg-orange-100/60" : "bg-white hover:bg-gray-50"
        }`}
      >
        <span
          aria-hidden="true"
          className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${unread ? "bg-orange-primary" : "bg-transparent"}`}
        />
        <span className="min-w-0 flex-1">
          <span className={`block text-sm ${unread ? "font-semibold text-gray-900" : "text-gray-700"}`}>
            {title}
          </span>
          {body && <span className="block text-xs text-gray-500 truncate">{body}</span>}
          <span className="block mt-0.5 text-[11px] text-gray-400">
            {item.createdAt && <time dateTime={item.createdAt}>{relativeTime(item.createdAt)}</time>}
            {unread && <span className="sr-only"> · Unread</span>}
          </span>
        </span>
      </button>
    </li>
  );
}

function PanelBody({ feed, onOpen }) {
  const { items, status, refresh } = feed;
  if (status === FEED_STATUS.ERROR && items.length === 0) {
    return (
      <div className="p-3">
        <RetryBanner message="Couldn't load notifications." onRetry={refresh} />
      </div>
    );
  }
  if (status === FEED_STATUS.LOADING && items.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading notifications</span>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <EmptyState
        className="py-8"
        icon={<Bell className="w-8 h-8 text-gray-300 mx-auto" aria-hidden="true" />}
        title="No notifications yet"
        hint="Lead, ticket and mention alerts show up here."
      />
    );
  }
  return (
    <ul className="divide-y divide-gray-100">
      {items.map((item) => (
        <NotificationItem key={item.id} item={item} onOpen={onOpen} />
      ))}
    </ul>
  );
}

/**
 * The bell at the top of the sidebar: unread badge, and a panel with the
 * newest 50 alerts (leads, tickets, mentions). Clicking an alert marks it
 * read and opens its target.
 *
 * @param {{ collapsed?: boolean }} props
 */
export default function NotificationBell({ collapsed = false }) {
  const feed = useNotificationFeed();
  const { unreadCount, notice, refresh, markRead, markAllRead } = feed;
  const navigate = useNavigate();
  const [position, setPosition] = useState(null);
  const open = position !== null;
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = useId();
  const headingId = useId();

  const close = useCallback((returnFocus) => {
    setPosition(null);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  const toggle = () => {
    if (open) {
      close(false);
      return;
    }
    setPosition(panelPosition(buttonRef.current));
    refresh();
  };

  useEffect(() => {
    if (!open) return undefined;
    panelRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") close(true);
    };
    const onPointerDown = (event) => {
      if (panelRef.current?.contains(event.target) || buttonRef.current?.contains(event.target)) return;
      close(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, close]);

  const openItem = (item) => {
    markRead(item.id);
    const { target } = describeNotification(item);
    if (!target) return;
    close(false);
    navigate(target);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        className="relative flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-primary"
        aria-label={`Notifications, ${unreadCount} unread`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        title={collapsed ? "Notifications" : undefined}
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-orange-primary text-white text-[10px] font-semibold leading-none"
          >
            {badgeLabel(unreadCount)}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <section
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-labelledby={headingId}
            tabIndex={-1}
            className="fixed z-[55] flex flex-col bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden focus:outline-none"
            style={{
              top: position.top,
              left: position.left,
              width: `min(${PANEL_WIDTH_PX}px, calc(100vw - ${VIEWPORT_MARGIN_PX * 2}px))`,
              maxHeight: `min(560px, calc(100vh - ${position.top + VIEWPORT_MARGIN_PX}px))`,
            }}
          >
            <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h2 id={headingId} className="text-sm font-semibold text-gray-800">
                  Notifications
                </h2>
                {feed.refreshing && feed.items.length > 0 && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" aria-label="Refreshing" />
                )}
              </div>
              <button
                type="button"
                onClick={markAllRead}
                disabled={unreadCount === 0}
                className="text-xs font-medium text-orange-600 hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
              >
                Mark all read
              </button>
            </header>
            {notice && (
              <p role="alert" className="px-4 py-2 text-xs text-danger-dark bg-danger-bg border-b border-danger/30">
                {notice}
              </p>
            )}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <PanelBody feed={feed} onOpen={openItem} />
            </div>
          </section>,
          document.body
        )}
    </>
  );
}
